import {
  Box,
  Divider,
  Grid,
  Link,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { Fragment, ReactElement, ReactNode, useState } from "react";
import { useAppDispatch } from "../../../app/hooks";
import formatCurrency from "../../../common/helpers/formatCurrency";
import { WAGE_REPORT_STATUS } from "../constants";
import { WageReportDeliveryDetails } from "./wageReportsAPI";
import { saveWageReportDeliverySalaryDate } from "./wageReportsSlice";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";

type Props = {
  deliveryDetails: WageReportDeliveryDetails;
  onClose: () => void;
  onOpenPayslip?: (payslipId: string) => void;
};

type WageReportCauseCodeOption = { value: string; name: string };

export const GridRow = ({
  title,
  value,
  sx,
}: {
  title: string;
  value?: string | ReactElement;
  sx?: SxProps;
}) => {
  return (
    <>
      <Grid item xs={5} sx={{ fontSize: 14, ...sx, fontWeight: 600 }}>
        {title}
      </Grid>
      <Grid item xs={7} sx={{ fontSize: 14, ...sx }}>
        {value}
      </Grid>
    </>
  );
};

export const DeliveryDetailsContent = ({
  deliveryDetails,
  onClose,
  onOpenPayslip,
}: Props) => {
  const [salaryDate, setSalaryDate] = useState<null | Date>(new Date());
  const [error, setError] = useState(false);

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const formatIncomeTypeCode = (code: string) => {
    const nameKey = `wagereportdeliveries:incomeTypeCodes.${code}`;
    return i18n.exists(nameKey) ? t(nameKey) : code;
  };

  const formatCarBenefit = (
    carBenefit: NonNullable<
      WageReportDeliveryDetails["transactionRows"][number]["carBenefit"]
    >,
  ) => {
    const typeKey = `wagereportdeliveries:carBenefitCodes.${carBenefit.carBenefitCode}`;
    const typeLabel = i18n.exists(typeKey)
      ? t(typeKey)
      : carBenefit.carBenefitCode;
    const ageGroup = carBenefit.ageGroupCode
      ? `, ${t("wagereportdeliveries:carBenefitAgeGroup")}: ${carBenefit.ageGroupCode}`
      : "";
    return `${t("wagereportdeliveries:carBenefit")} (${t("wagereportdeliveries:carBenefitType")}: ${typeLabel}${ageGroup})`;
  };

  const formatOptionalDate = (value?: string | null) =>
    value ? format(new Date(value), leadingZerosDateFormat) : "";

  const deliveryStatus = WAGE_REPORT_STATUS.find(
    (status) => status.value === deliveryDetails.status,
  )?.name;

  const onSave = () => {
    if (salaryDate) {
      const formattedDate = format(salaryDate, "yyyy-MM-dd");

      dispatch(
        saveWageReportDeliverySalaryDate({
          wageReportDeliveryId: deliveryDetails.id,
          salaryDate: formattedDate,
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          onClose();
        }
      });
    }
  };

  const absences = deliveryDetails.absences;

  const rawPaidCauseCodes = t("wagereportdeliveries:paidCauseCodes", {
    returnObjects: true,
  });
  const causePaidCodeOptions: readonly WageReportCauseCodeOption[] = Array.isArray(
    rawPaidCauseCodes,
  )
    ? (rawPaidCauseCodes as WageReportCauseCodeOption[])
    : [];

  const rawUnpaidCauseCodes = t("wagereportdeliveries:unpaidCauseCodes", {
    returnObjects: true,
  });
  const causeUnpaidCodeOptions: readonly WageReportCauseCodeOption[] =
    Array.isArray(rawUnpaidCauseCodes)
      ? (rawUnpaidCauseCodes as WageReportCauseCodeOption[])
      : [];

  return (
    <DialogContentWrapper
      title={t("payslips:wageReport")}
      onCancel={onClose}
      onSave={deliveryStatus === "notInIncomeRegister" ? onSave : undefined}
      customCancelButtonLabel={t("common:close")}
      customSaveButtonLabel={t("common:save")}
    >
      <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          sx={{
            height: 34,
            width: 34,
          }}
          alt="Eezy logo"
          src="/assets/eezy_round_logo.svg"
        />
      </Grid>
      <Grid item xs={10} container>
        <GridRow
          title={t("payslips:reportId")}
          value={deliveryDetails.reportId}
          sx={{ fontSize: 14 }}
        />
        <GridRow
          title={t("payslips:incomeRegisterReportId")}
          value={deliveryDetails.incomeRegisterReportId}
          sx={{ fontSize: 14 }}
        />
        <GridRow
          title={t("payslips:payslipNumberLong")}
          value={deliveryDetails.payslipNumber.toString()}
          sx={{ fontSize: 14 }}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 1.5 }} />
      </Grid>
      <Grid container item>
        <GridRow
          title={t("payslips:salaryDate")}
          value={
            deliveryDetails.salaryDate ? (
              format(
                new Date(deliveryDetails.salaryDate),
                leadingZerosDateFormat,
              )
            ) : (
              <DesktopDatePicker
                value={salaryDate}
                onChange={(value: Date | null) => setSalaryDate(value)}
                format={leadingZerosDateFormat}
                slots={{ textField: TextField }}
                onError={(error) => setError(!!error)}
                slotProps={{
                  textField: {
                    error: error,
                    size: "small",
                  },
                }}
              />
            )
          }
          sx={{ fontSize: 16 }}
        />
        <GridRow
          title={t("payslips:payer")}
          value={deliveryDetails.eezyCompanyName}
          sx={{ fontSize: 16 }}
        />
        <GridRow
          title={t("payslips:status")}
          value={
            deliveryStatus
              ? t("payslips:" + deliveryStatus)!!
              : deliveryDetails.status
          }
          sx={{ fontSize: 16 }}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 1.5 }} />
      </Grid>
      <Grid container item>
        <GridRow
          title={t("payslips:recipient")}
          value={deliveryDetails.talentName}
          sx={{ fontSize: 16 }}
        />
        <GridRow
          title={t("payslips:ssn")}
          value={deliveryDetails.talentSsn}
          sx={{ fontSize: 16 }}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ marginY: 1.5 }} />
      </Grid>
      <Grid container item xs={12}>
        <Grid item xs={12}>
          <Typography variant="header" fontSize={18} fontWeight={600} pb={1}>
            {t("wagereportdeliveries:incomeTypes")}
          </Typography>
        </Grid>
        <Grid item xs={4} sx={{ fontWeight: 600 }}>
          {t("wagereportdeliveries:incomeType")}
        </Grid>
        <Grid item xs={2} sx={{ fontWeight: 600 }}>
          {t("payslips:amount")}
        </Grid>
        <Grid item xs={3} sx={{ fontWeight: 600 }}>
          {t("payslips:description")}
        </Grid>
        <Grid item xs={3} sx={{ fontWeight: 600 }}>
          {t("payslips:earningPeriod")}
        </Grid>
        {deliveryDetails.transactionRows.map((row, i) => (
          <Fragment key={i}>
            <Grid item xs={4}>
              {formatIncomeTypeCode(row.transactionCode)}
            </Grid>
            <Grid item xs={2}>
              {formatCurrency(row.amount)}
            </Grid>
            <Grid item xs={3}>
              {(
                [
                  row.unjustEnrichment &&
                    t("wagereportdeliveries:unjustEnrichment"),
                  row.recoveredOnPayslipId && (
                    <>
                      {t("wagereportdeliveries:recoveredOnPayslip")}:{" "}
                      <Link
                        component="button"
                        type="button"
                        onClick={() =>
                          onOpenPayslip?.(row.recoveredOnPayslipId!)
                        }
                        sx={{
                          cursor: "pointer",
                          verticalAlign: "baseline",
                          font: "inherit",
                        }}
                      >
                        {t("common:open")}
                      </Link>
                    </>
                  ),
                  row.recoveryDetails?.date &&
                    `${t("wagereportdeliveries:recovery")}: ${format(new Date(row.recoveryDetails.date), leadingZerosDateFormat)}`,
                  row.recoveryDetails?.originalPayslips &&
                    row.recoveryDetails.originalPayslips.length > 0 && (
                      <div>
                        <div>
                          {t("wagereportdeliveries:originalPayslips")}:
                        </div>
                        {row.recoveryDetails.originalPayslips.map(
                          (originalPayslip) => (
                            <div key={originalPayslip.payslipId}>
                              <Link
                                component="button"
                                type="button"
                                onClick={() =>
                                  onOpenPayslip?.(originalPayslip.payslipId)
                                }
                                sx={{
                                  cursor: "pointer",
                                  verticalAlign: "baseline",
                                  font: "inherit",
                                }}
                              >
                                {t("common:open")}
                              </Link>
                              {` (${format(new Date(originalPayslip.period.start), leadingZerosDateFormat)} - ${format(new Date(originalPayslip.period.end), leadingZerosDateFormat)})`}
                            </div>
                          ),
                        )}
                      </div>
                    ),
                  row.recoveryDetails?.taxWithHolding &&
                    `${t("wagereportdeliveries:recoveryTaxWithholding")}: ${formatCurrency(row.recoveryDetails.taxWithHolding)}`,
                  row.recoveryDetails?.taxAtSource &&
                    `${t("wagereportdeliveries:recoveryTaxAtSource")}: ${formatCurrency(row.recoveryDetails.taxAtSource)}`,
                  row.carBenefit && formatCarBenefit(row.carBenefit),
                  row.mealBenefit &&
                    `${t("wagereportdeliveries:mealBenefit")} (${t("wagereportdeliveries:mealBenefitTaxValue")}: ${row.mealBenefit.taxValue ? t("common:yes") : t("common:no")}${
                      row.mealBenefit.isCompensated != null
                        ? `, ${t("wagereportdeliveries:mealBenefitCompensated")}: ${row.mealBenefit.isCompensated ? t("common:yes") : t("common:no")}`
                        : ""
                    })`,
                  row.otherBenefit &&
                    `${t("wagereportdeliveries:otherBenefit")}: ${row.otherBenefit.benefitCodes.join(", ")}`,
                  row.kmAllowance &&
                    `${t("wagereportdeliveries:kmAllowance")}: ${row.kmAllowance.kilometers}`,
                  row.dailyAllowance &&
                    `${t("wagereportdeliveries:dailyAllowance")}: ${row.dailyAllowance.allowanceCodes.join(", ")}`,
                ] as ReactNode[]
              )
                .filter(Boolean)
                .map((line, idx) => (
                  <div key={idx}>{line}</div>
                ))}
            </Grid>
            <Grid item xs={3}>
              {row.earningPeriods
                ?.map(
                  (ep) =>
                    `${format(new Date(ep.start), leadingZerosDateFormat)} - ${format(new Date(ep.end), leadingZerosDateFormat)}`,
                )
                .join(", ") ?? ""}
            </Grid>
          </Fragment>
        ))}
      </Grid>
      {absences && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ marginY: 1.5 }} />
          </Grid>
          <Grid container item xs={12}>
            <Grid item xs={12}>
              <Typography
                variant="header"
                fontSize={18}
                fontWeight={600}
                pb={1}
              >
                {absences.reportRange
                  ? t("wagereportdeliveries:absenceRange") +
                    " " +
                    format(
                      new Date(absences.reportRange.start),
                      leadingZerosDateFormat,
                    ) +
                    " - " +
                    format(
                      new Date(absences.reportRange.end),
                      leadingZerosDateFormat,
                    )
                  : ""}
              </Typography>
            </Grid>

            {(absences.paidPeriods?.length ?? 0) > 0 && (
              <>
                <Grid item xs={12} sx={{ fontWeight: 600, pt: 1, pb: 0.5 }}>
                  {t("wagereportdeliveries:paidAbsences")}
                </Grid>
                <Grid item xs={3} sx={{ fontWeight: 600 }}>
                  {t("wagereportdeliveries:absencePeriod")}
                </Grid>
                <Grid item xs={1} sx={{ fontWeight: 600 }}>
                  {t("wagereportdeliveries:absenceDays")}
                </Grid>
                <Grid item xs={2} sx={{ fontWeight: 600 }}>
                  {t("payslips:amount")}
                </Grid>
                <Grid item xs={2} sx={{ fontWeight: 600 }}>
                  {t("wagereportdeliveries:causeCode")}
                </Grid>
                <Grid item xs={4} sx={{ fontWeight: 600 }}>
                  {t("wagereportdeliveries:reimbApp")}
                </Grid>

                {absences.paidPeriods?.map((row, i) => (
                  <Fragment key={i}>
                    <Grid item xs={3}>
                      {formatOptionalDate(row.range.start)}
                      {(row.untilDate || row.range.end) && (
                        <>
                          {" - "}
                          {row.untilDate ? (
                            <Tooltip
                              title={t(
                                "wagereportdeliveries:estimatedUntilDateTooltip",
                              )}
                            >
                              <Box
                                component="span"
                                sx={{
                                  fontStyle: "italic",
                                  color: "text.secondary",
                                  textDecoration: "underline",
                                  textDecorationStyle: "dotted",
                                  cursor: "help",
                                }}
                              >
                                {formatOptionalDate(row.untilDate)}
                              </Box>
                            </Tooltip>
                          ) : (
                            formatOptionalDate(row.range.end)
                          )}
                        </>
                      )}
                    </Grid>
                    <Grid item xs={1}>
                      {row.days}
                    </Grid>
                    <Grid item xs={2}>
                      {formatCurrency(row.expense.toString())}
                    </Grid>
                    <Grid item xs={2}>
                      {causePaidCodeOptions.find(
                        (code) => row.causeCode === code.value,
                      )?.name ?? row.causeCode}
                    </Grid>
                    <Grid item xs={4}>
                      {row.reimbursementApplication
                        ? `${t("wagereportdeliveries:paymentType")} ${row.reimbursementApplication.paymentType}, ${t("wagereportdeliveries:paymentSpecifier")} ${row.reimbursementApplication.paymentSpecifier}`
                        : ""}
                    </Grid>
                  </Fragment>
                ))}
              </>
            )}

            {(absences.unpaidPeriods?.length ?? 0) > 0 && (
              <>
                <Grid item xs={12} sx={{ fontWeight: 600, pt: 1, pb: 0.5 }}>
                  {t("wagereportdeliveries:unpaidAbsences")}
                </Grid>

                <Grid item xs={3} sx={{ fontWeight: 600 }}>
                  {t("wagereportdeliveries:absencePeriod")}
                </Grid>
                <Grid item xs={1} sx={{ fontWeight: 600 }}>
                  {t("wagereportdeliveries:absenceDays")}
                </Grid>
                <Grid item xs={2} sx={{ fontWeight: 600 }}>
                  {t("payslips:amount")}
                </Grid>
                <Grid item xs={2} sx={{ fontWeight: 600 }}>
                  {t("wagereportdeliveries:causeCode")}
                </Grid>
                <Grid item xs={4} sx={{ fontWeight: 600 }} />

                {absences.unpaidPeriods?.map((row, i) => (
                  <Fragment key={i}>
                    <Grid item xs={3}>
                      {formatOptionalDate(row.range.start)}
                      {row.range.end && (
                        <> - {formatOptionalDate(row.range.end)}</>
                      )}
                    </Grid>
                    <Grid item xs={1}>
                      {row.days}
                    </Grid>
                    <Grid item xs={2} />
                    <Grid item xs={2}>
                      {causeUnpaidCodeOptions.find(
                        (code) => row.causeCode === code.value,
                      )?.name ?? row.causeCode}
                    </Grid>
                    <Grid item xs={4} />
                  </Fragment>
                ))}
              </>
            )}
          </Grid>
        </>
      )}
    </DialogContentWrapper>
  );
};
